import type { UseHookLogReturnType } from '@/types/composables/HookLogTypes';
import type { HookLogType } from '@/types/HookLogType';
import { useTranslation } from './useTranslation';
import { HookStatusEnum } from '@/types/enums/HookStatusEnum';
import { HookTriggerEnum } from '@/types/enums/HookTriggerEnum';
import { HookTypeEnum } from '@/types/enums/HookTypeEnum';

export const useHookLog = (): UseHookLogReturnType => {
    const { t } = useTranslation();

    const getStatusColor = (status: HookStatusEnum): string => {
        switch (status) {
        case HookStatusEnum.Success:
            return 'success';
        case HookStatusEnum.Failed:
            return 'error';
        case HookStatusEnum.Pending:
            return 'warning';
        default:
            return 'default';
        }
    };

    const getTriggerTypeLabel = (triggerType: HookTriggerEnum): string => {
        switch (triggerType) {
        case HookTriggerEnum.Arrival:
            return t('hooks.triggers.arrival');
        case HookTriggerEnum.Departure:
            return t('hooks.triggers.departure');
        default:
            return triggerType;
        }
    };

    const getStatusLabel = (status: HookStatusEnum): string => {
        switch (status) {
        case HookStatusEnum.Success:
            return t('hookLogs.statuses.success');
        case HookStatusEnum.Failed:
            return t('hookLogs.statuses.failed');
        case HookStatusEnum.Pending:
            return t('hookLogs.statuses.pending');
        default:
            return status;
        }
    };

    const formatExecutionTime = (timeMs: number): string => {
        if (timeMs === 0) {
            return '-';
        }

        return `${timeMs}ms`;
    };

    const isDbHookLog = (hookLog: HookLogType): boolean => {
        return hookLog.hook_type === HookTypeEnum.DB;
    };

    const isHttpHookLog = (hookLog: HookLogType): boolean => {
        return hookLog.hook_type === HookTypeEnum.HTTP;
    };

    const getTypeSpecificFields = (hookLog: HookLogType): Record<string, unknown> => {
        if (isHttpHookLog(hookLog)) {
            return {
                url: hookLog.request_data,
                headers: hookLog.response_data,
                http_status_code: hookLog.http_status_code,
            };
        }

        if (isDbHookLog(hookLog)) {
            return {
                query_data: hookLog.request_data,
                result_data: hookLog.response_data,
            };
        }

        return {};
    };

    return {
        getStatusColor,
        getTriggerTypeLabel,
        getStatusLabel,
        formatExecutionTime,
        isDbHookLog,
        isHttpHookLog,
        getTypeSpecificFields,
    };
};
